import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(4),
	},
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(2),
	},
	bubble: {
		maxWidth: "70%",
		padding: theme.spacing(1, 1.5),
		borderRadius: 8,
		marginBottom: theme.spacing(1),
	},
	bubbleIn: {
		backgroundColor: theme.palette.background.default,
		marginRight: "auto",
	},
	bubbleOut: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		marginLeft: "auto",
	},
	thread: {
		maxHeight: "60vh",
		overflowY: "auto",
		display: "flex",
		flexDirection: "column",
	},
	composer: {
		display: "flex",
		gap: theme.spacing(1),
		width: "100%",
		padding: theme.spacing(0, 1),
	},
	media: {
		maxWidth: "100%",
		maxHeight: 260,
		borderRadius: 4,
		display: "block",
	},
}));

const MessageMedia = ({ message, classes }) => {
	if (!message.mediaUrl) return <>{message.body}</>;

	if (message.mediaType === "image" || message.mediaType === "sticker") {
		return (
			<a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
				<img src={message.mediaUrl} alt="mídia" className={classes.media} />
			</a>
		);
	}
	if (message.mediaType === "video") {
		// eslint-disable-next-line jsx-a11y/media-has-caption
		return <video src={message.mediaUrl} controls className={classes.media} />;
	}
	if (message.mediaType === "audio" || message.mediaType === "ptt") {
		// eslint-disable-next-line jsx-a11y/media-has-caption
		return <audio src={message.mediaUrl} controls />;
	}
	return (
		<a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
			{message.body || "Baixar arquivo"}
		</a>
	);
};

const TenantChats = () => {
	const classes = useStyles();
	const history = useHistory();
	const { tenantId } = useParams();
	const [tickets, setTickets] = useState([]);
	const [messagesModalOpen, setMessagesModalOpen] = useState(false);
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [messages, setMessages] = useState([]);
	const [draft, setDraft] = useState("");
	const [sending, setSending] = useState(false);

	const fetchTickets = async () => {
		try {
			const { data } = await api.get(`/tenants/${tenantId}/tickets`);
			setTickets(data);
		} catch (err) {
			toastError(err);
		}
	};

	useEffect(() => {
		fetchTickets();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tenantId]);

	const fetchMessages = async ticketId => {
		try {
			const { data } = await api.get(
				`/tenants/${tenantId}/tickets/${ticketId}/messages`
			);
			setMessages(data);
		} catch (err) {
			toastError(err);
		}
	};

	const openMessages = async ticket => {
		setSelectedTicket(ticket);
		setMessagesModalOpen(true);
		fetchMessages(ticket.id);
	};

	const handleSend = async () => {
		const body = draft.trim();
		if (!body || !selectedTicket) return;
		setSending(true);
		try {
			// Owner steps into the conversation "as the one handling it" —
			// signed with the Owner's own name, same as any human agent.
			await api.post(
				`/tenants/${tenantId}/tickets/${selectedTicket.id}/messages`,
				{ body }
			);
			setDraft("");
			await fetchMessages(selectedTicket.id);
		} catch (err) {
			toastError(err);
		} finally {
			setSending(false);
		}
	};

	return (
		<div className={classes.root}>
			<Container maxWidth="lg">
				<div className={classes.header}>
					<Typography variant="h5">Chats da empresa</Typography>
					<Button onClick={() => history.push("/admin/tenants")}>Voltar</Button>
				</div>
				<Paper variant="outlined">
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Protocolo</TableCell>
								<TableCell>Contato</TableCell>
								<TableCell>Atendente</TableCell>
								<TableCell>Fila</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="center">Ação</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{tickets.map(ticket => (
								<TableRow key={ticket.id}>
									<TableCell>{ticket.protocol || "-"}</TableCell>
									<TableCell>{ticket.contact?.name}</TableCell>
									<TableCell>{ticket.user?.name || "-"}</TableCell>
									<TableCell>{ticket.queue?.name || "-"}</TableCell>
									<TableCell>{ticket.status}</TableCell>
									<TableCell align="center">
										<Button size="small" onClick={() => openMessages(ticket)}>
											Abrir chat
										</Button>
									</TableCell>
								</TableRow>
							))}
							{tickets.length === 0 && (
								<TableRow>
									<TableCell colSpan={6} align="center">
										Nenhum ticket ainda.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</Paper>
			</Container>

			<Dialog
				open={messagesModalOpen}
				onClose={() => setMessagesModalOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					Conversa com {selectedTicket?.contact?.name}
				</DialogTitle>
				<DialogContent className={classes.thread}>
					{messages.map(message => (
						<div
							key={message.id}
							className={`${classes.bubble} ${
								message.fromMe ? classes.bubbleOut : classes.bubbleIn
							}`}
						>
							<MessageMedia message={message} classes={classes} />
						</div>
					))}
					{messages.length === 0 && (
						<Typography variant="body2" color="textSecondary">
							Sem mensagens.
						</Typography>
					)}
				</DialogContent>
				<DialogActions>
					<div className={classes.composer}>
						<TextField
							fullWidth
							size="small"
							placeholder="Responder como se você estivesse atendendo..."
							value={draft}
							onChange={e => setDraft(e.target.value)}
							onKeyDown={e => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									handleSend();
								}
							}}
						/>
						<Button
							variant="contained"
							color="primary"
							disabled={sending || !draft.trim()}
							onClick={handleSend}
						>
							Enviar
						</Button>
					</div>
				</DialogActions>
				<DialogActions>
					<Button onClick={() => setMessagesModalOpen(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default TenantChats;
